import { FC, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { SearchContextProvider, useSearchContext } from "../../../context/searchContext";
import useI18n from "../../../hooks/useTranslations";
import { CustomError } from "../../../http/errors";
import { useSystemsApi } from "../../../http/useSystems";
import Center from "../../ui/Center";
import ErrorText from "../../ui/ErrorText";
import FlexContent from "../../ui/FlexContent";
import Header, { HeaderPath } from "../../ui/Header";
import LoadingSpinner from "../../ui/LoadingSpinner";
import BorderSettings from "./border/BorderSettings";
import HRailSettings from "./hrail/HRailSettings";
import LadstepSettings from "./ladstep/LadstepSettings";
import OverpassSettings from "./overpass/OverpassSettings";
import PatrolSettings from "./patrol/PatrolSettings";
import SafetyNetSettings from "./safetyNet/SafetyNetSettings";
import VertigripSettings from "./vertigrip/VertigripSettings";

export const VERTIGRIP = "Vertigrip";
export const PATROL = "Patrol";
export const BORDER = "Border";
export const HRAIL = "H-Rail";
export const SAFETYNET = "Safety-Net";
export const LADSTEP = "Ladstep";
export const OVERPASS = "Overpass";

const AdminSystemSettings: FC = () => {
  const i18n = useI18n();
  const { systemId } = useParams<{ systemId: string }>();
  const systemApi = useSystemsApi();

  const aborter = useRef(new AbortController());
  const { error, data, trigger } = systemApi.getAdminSystem(aborter.current.signal);

  useEffect(() => {
    systemId && trigger(systemId);
  }, [systemId]);

  if (!data && !error) {
    return (
      <Center>
        <LoadingSpinner />
      </Center>
    );
  }

  const pathTitle = getSystemTypeName(data?.id);

  const path = [
    { url: `/admin/systems`, display: i18n.translation.systems.all },
    { url: `/admin/systems/${systemId}`, display: pathTitle },
    { display: i18n.translation.common.settings },
  ];

  return (
    <SearchContextProvider>
      <AdminSystemSettingsContent
        title={data?.name ?? i18n.translation.common.loading}
        error={error}
        path={path}
        systemId={data?.id}
      />
    </SearchContextProvider>
  );
};

type AdminSystemSettingsContentProps = {
  title: string;
  error?: CustomError;
  path: HeaderPath[];
  systemId?: string;
};

const AdminSystemSettingsContent: FC<AdminSystemSettingsContentProps> = ({ title, error, path, systemId }) => {
  const i18n = useI18n();
  const { actions, title: tabTitle } = useSearchContext();

  return (
    <>
      <Header
        title={tabTitle ? `${getSystemTypeName(systemId)} · ${tabTitle}` : title}
        error={error}
        path={path}
        buttons={actions}
      />
      {systemId == "vertigrip" ? (
        <VertigripSettings />
      ) : systemId == "border" ? (
        <BorderSettings />
      ) : systemId == "patrol" ? (
        <PatrolSettings />
      ) : systemId == "h-rail" ? (
        <HRailSettings />
      ) : systemId == "safety-net" ? (
        <SafetyNetSettings />
      ) : systemId == "ladstep" ? (
        <LadstepSettings />
      ) : systemId == "overpass" ? (
        <OverpassSettings />
      ) : (
        <FlexContent padding>
          <ErrorText text={i18n.translation.common.genericError} />
        </FlexContent>
      )}
    </>
  );
};

export default AdminSystemSettings;

export const getSystemTypeName = (systemId: string | undefined | null) => {
  if (systemId === "vertigrip") {
    return VERTIGRIP;
  } else if (systemId === "border") {
    return BORDER;
  } else if (systemId === "patrol") {
    return PATROL;
  } else if (systemId === "h-rail") {
    return HRAIL;
  } else if (systemId === "safety-net") {
    return SAFETYNET;
  } else if (systemId === "ladstep") {
    return LADSTEP;
  } else if (systemId === "overpass") {
    return OVERPASS;
  }

  return "";
};
